/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import i18n from '../../i18n/i18n';
import { useStore } from '../../stores/common';
import { usePrimitiveStore } from '../../stores/commonPrimitive';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { App } from 'antd';
import NewProjectItem from './createNewProjectItem.tsx';
import { OpenProjectItem } from './openProjectItem.tsx';
import { SaveProjectItem } from './saveProjectItem.tsx';
import { SaveProjectAsItem } from './saveProjectAsItem.tsx';
import { MainMenuItem, MainSubMenu } from '../menuItem.tsx';
import { MenuDivider } from '@szhsin/react-menu';
import { Util } from '../../Util.ts';
import { HookAPI } from 'antd/lib/modal/useModal';
import { setMessage } from '../../helpers.tsx';
import { t } from 'i18next';
import { useLanguage } from '../../hooks.ts';
import * as Selector from '../../stores/selector';
import { RecentProjectInfo } from '../../types.ts';
import { askToSaveThen } from '../../saveReminder.tsx';

export const askToCreateProject = (modal: HookAPI) => {
  askToSaveThen(modal, createProject);
};

const createProject = () => {
  usePrimitiveStore.getState().setCreateProjectDialog(true);
  if (useStore.getState().loggable) useStore.getState().logAction('Create New Project');
};

export const askToOpenProject = (modal: HookAPI) => {
  askToSaveThen(modal, openProject);
};

const openProject = () => {
  usePrimitiveStore.getState().set((state) => {
    state.showProjectsFlag = true;
  });
  useStore.getState().set((state) => {
    state.selectedFloatingWindow = 'projectListPanel';
    if (state.loggable) state.logAction('Open Project');
  });
};

// Open a specific project straight from the Recent Projects submenu. Parks
// the target (owner + title) on the store and flips the flag CloudManager
// watches (it owns loadProject).
const openRecentProject = (record: RecentProjectInfo) => {
  useStore.getState().set((state) => {
    state.recentProjectToOpen = record;
    if (state.loggable) state.logAction('Open Recent Project: ' + record.title);
  });
  usePrimitiveStore.getState().set((state) => {
    state.openRecentProjectFlag = true;
  });
};

export const askToOpenRecentProject = (modal: HookAPI, record: RecentProjectInfo) => {
  askToSaveThen(
    modal,
    () => openRecentProject(record),
    // Yes → save the current project first, then open the recent one (a
    // sequenced save-then-open, not fire-and-forget). The owner travels on
    // recentProjectToOpen, which saveProject's tail honors.
    () => {
      useStore.getState().set((state) => {
        state.recentProjectToOpen = record;
      });
      usePrimitiveStore.getState().set((state) => {
        state.saveAndThenOpenProjectFlag = true;
      });
    },
  );
};

export const saveProject = () => {
  usePrimitiveStore.getState().setSaveProjectFlag(true);
  if (useStore.getState().loggable) useStore.getState().logAction('Save Project');
};

export const saveProjectAs = () => {
  usePrimitiveStore.getState().setSaveProjectAsDialog(true);
  if (useStore.getState().loggable) useStore.getState().logAction('Save Project As');
};

interface Props {
  isMac: boolean;
}

const ProjectMenu = ({ isMac }: Props) => {
  const lang = useLanguage();
  const generating = usePrimitiveStore(Selector.generating);
  // Subscribe reactively — otherwise the menu captures whatever was in the
  // store on its first render and doesn't update when the user switches
  // between projects (e.g. Share vs. Remove from Commons would be stale).
  const projectTitle = useStore((s) => s.projectState.title);
  const projectOwner = useStore((s) => s.projectState.owner);
  const isShared = useStore((s) => s.projectState.isPublic === true);
  const user = useStore((s) => s.user);
  const recentProjects = useStore(Selector.recentlyOpenedProjects);
  const { modal } = App.useApp();

  // Sharing acts on a project the signed-in user owns. Hidden when viewing
  // someone else's project (e.g. an Examples doc or a Commons link) — the
  // user would need to Save-As to their own collection first.
  const canShare = !!user.uid && !!projectTitle && projectOwner === user.uid;

  const confirmShare = () => {
    modal.confirm({
      title: i18n.t('projectListPanel.DoYouReallyWantToShareProject', lang) + ' "' + projectTitle + '"?',
      content: i18n.t('projectListPanel.ShareProjectExplanation', lang),
      icon: <QuestionCircleOutlined />,
      onOk: () => {
        usePrimitiveStore.getState().set((state) => {
          state.shareProjectFlag = true;
        });
      },
    });
  };

  const confirmRemoveFromCommons = () => {
    modal.confirm({
      title: i18n.t('projectListPanel.DoYouReallyWantToRemoveProjectFromCommons', lang) + ' "' + projectTitle + '"?',
      icon: <QuestionCircleOutlined />,
      onOk: () => {
        usePrimitiveStore.getState().set((state) => {
          state.removeFromCommonsFlag = true;
        });
      },
    });
  };

  return (
    <MainSubMenu label={t('menu.projectSubMenu', lang)}>
      <NewProjectItem isMac={isMac} generating={generating} />

      <OpenProjectItem isMac={isMac} askToOpenProject={askToOpenProject} />

      {recentProjects.length > 0 && (
        <MainSubMenu label={t('menu.project.RecentProjects', lang)}>
          {recentProjects.map((p) => (
            <MainMenuItem key={`${p.owner}/${p.title}`} onClick={() => askToOpenRecentProject(modal, p)}>
              {p.title}
            </MainMenuItem>
          ))}
        </MainSubMenu>
      )}

      {!!projectTitle && <SaveProjectItem isMac={isMac} saveProject={saveProject} />}

      <SaveProjectAsItem isMac={isMac} saveProjectAs={saveProjectAs} />

      {user.uid && projectTitle && <MenuDivider />}

      {user.uid && projectTitle && (
        <MainMenuItem
          onClick={() => {
            if (!user.uid || !projectTitle) return;
            Util.generateProjectLink(user.uid, projectTitle, () => {
              setMessage('success', i18n.t('projectListPanel.ProjectLinkGeneratedInClipBoard', lang) + '.');
            });
          }}
        >
          {i18n.t('projectListPanel.GenerateProjectLink', lang)}
        </MainMenuItem>
      )}

      {canShare && !isShared && (
        <MainMenuItem onClick={confirmShare}>{i18n.t('projectListPanel.ShareToCommons', lang)}</MainMenuItem>
      )}

      {canShare && isShared && (
        <MainMenuItem onClick={confirmRemoveFromCommons}>
          {i18n.t('projectListPanel.RemoveFromCommons', lang)}
        </MainMenuItem>
      )}

      {user.uid && projectTitle && (
        <MainMenuItem
          onClick={() => {
            usePrimitiveStore.getState().set((state) => {
              state.showSubmitToClassModal = true;
            });
          }}
        >
          {i18n.t('classroom.SubmitProjectToClass', lang)}
        </MainMenuItem>
      )}

      {user.uid && projectTitle && <MenuDivider />}

      {/* A lesson plan is generated FROM the open project, so only offer it
          when there's a project loaded (a titled scene). "My Plans" below
          stays available without one so saved plans are always reachable. */}
      {user.uid && projectTitle && (
        <MainMenuItem
          onClick={() => {
            usePrimitiveStore.getState().set((state) => {
              // Null the carrier so the modal opens in create-from-project mode
              // even if a previously-opened saved plan left it set.
              state.lessonPlanToOpen = null;
              state.showLessonPlanModal = true;
            });
            if (useStore.getState().loggable) useStore.getState().logAction('Create Lesson Plan');
          }}
        >
          {i18n.t('lessonPlans.MenuCreate', lang)}
        </MainMenuItem>
      )}

      {user.uid && (
        <MainMenuItem
          onClick={() => {
            usePrimitiveStore.getState().set((state) => {
              state.showLessonPlanListPanel = true;
            });
          }}
        >
          {i18n.t('lessonPlans.MenuMyPlans', lang)}
        </MainMenuItem>
      )}
    </MainSubMenu>
  );
};

export default ProjectMenu;
